import { useQuery } from '@tanstack/react-query';
import { LeadDetailBody as SharedLeadDetailBody } from '@viq-admin/screens';
import { space } from '@viq-admin/ui';
import { RequireCan } from '../../components/RequireCan';
import { useAbility } from '../../ability/AbilityContext';
import { leadDetailApi, discoveryClosures } from './leadDetailApi';

/**
 * Super-admin lead detail body. Renders the SHARED, parameterized Lead Detail
 * component wired to the super-admin plane's endpoints via `leadDetailApi`.
 * `read Lead` gates viewing.
 *
 * THREE independent capability gates drive what the actor may mutate — the
 * leads-only edit carve-out (viq-admin#13 / viq-backend#52):
 *   - `update Lead` → edit a lead's own fields, its notes, and its Discovery
 *     answers. Granted to BOTH super_admin_readonly (the carve-out) and
 *     super_admin.
 *   - `delete Lead` → remove a single Discovery answer. Falls under `manage`,
 *     so super_admin only — matches the backend gate on the DELETE route.
 *   - `manage Lead` → destructive lead-level Delete/Restore. super_admin only.
 *
 * Defense-in-depth (Task 5, extended Tasks 10 and 17): the actor doesn't just
 * get buttons hidden via `readOnly` — each mutating CLOSURE (updateLead,
 * updateNote, updateDiscoveryAnswer, deleteDiscoveryAnswer, archiveLead,
 * restoreLead) is withheld from the shared component's `api` unless its own
 * gate passes, so a bug in the shared component's render gate still can't
 * reach a working Save/Delete/Restore/note-edit/Discovery-edit path the
 * backend would (correctly) 403. Non-mutating methods (fetchLeadDetail,
 * fetchDiscovery, photos) are always preserved via the spread.
 *
 * The Discovery closures additionally need the lead's OWN tenant (see
 * `discoveryClosures` in leadDetailApi.ts) — a super_admin can open a lead
 * outside the currently-selected scope, and the write must still land on
 * that lead's tenant, not the global company-scope picker's. That tenant
 * isn't known until the lead detail loads, so this reads it via the SAME
 * query key + fetcher the shared body itself uses (`['leadDetail', id]` /
 * `leadDetailApi.fetchLeadDetail`) — sharing that cache entry rather than
 * costing a second request. `companyId` is undefined on first paint; the
 * Discovery closures simply aren't supplied until it resolves.
 */
export function LeadDetailBody({ id }: { id: string }) {
  const ability = useAbility();
  const canEdit = ability.can('update', 'Lead'); // edit fields/notes/Discovery answers (readonly + admin)
  const canDestroy = ability.can('manage', 'Lead'); // delete/restore the lead (admin only)
  const canDeleteAnswer = ability.can('delete', 'Lead'); // delete one Discovery answer (admin only)

  const { data } = useQuery({
    queryKey: ['leadDetail', id],
    queryFn: () => leadDetailApi.fetchLeadDetail(id),
    enabled: !!id,
  });
  const companyId = data?.person.companyId;
  const discovery = companyId ? discoveryClosures(companyId) : undefined;

  const api = {
    ...leadDetailApi,
    updateLead: canEdit ? leadDetailApi.updateLead : undefined,
    updateNote: canEdit ? leadDetailApi.updateNote : undefined,
    updateDiscoveryAnswer: canEdit ? discovery?.updateDiscoveryAnswer : undefined,
    deleteDiscoveryAnswer: canDeleteAnswer ? discovery?.deleteDiscoveryAnswer : undefined,
    archiveLead: canDestroy ? leadDetailApi.archiveLead : undefined,
    restoreLead: canDestroy ? leadDetailApi.restoreLead : undefined,
  };
  // Only fully read-only (no action bar) when the actor can neither edit nor destroy.
  const readOnly = !canEdit && !canDestroy;
  return (
    <RequireCan I="read" a="Lead">
      <div style={{ padding: space.lg }}>
        <SharedLeadDetailBody id={id} api={api} readOnly={readOnly} />
      </div>
    </RequireCan>
  );
}
